/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: RecordingManagementPage.tsx
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Recording Management main page with comprehensive CRUD operations,
    file upload, AI TTS generation, and domain-based management.

 🔧 Notes:
    - Implements complete recording management with CRUD operations
    - Supports file upload and AI TTS generation
    - Handles domain-based recording organization
    - Integrates with DataGrid for data display
    - Provides comprehensive validation and error handling
    - Supports multi-tenant recording management

 🔁 Last Updated: 2025-01-XX by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic } from 'lucide-react';

import DataGridSimple from '../../components/ui/DataGridSimple';
import ConfirmationModal from '../../components/ui/ConfirmationModal';
import { useNotifications } from '../../contexts/NotificationContext';
import { useI18n } from '../../hooks/useI18n';
import { ModulePageContainer } from '../../components/layouts';
import { VoiceRecording, RecordingFilters } from './types';
import { useRecordingManagementList, useAudioPlayback } from './hooks';
import { useRecordingManagementColumns } from './components';
import { RecordingManagementActions } from './components';
import { deleteRecording, downloadRecording } from './api';

export default function RecordingManagementPage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { showSuccess, showError } = useNotifications();

  // State management
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordingToDelete, setRecordingToDelete] = useState<VoiceRecording | null>(null);
  const [filters] = useState<RecordingFilters>({});

  // Use hooks
  const availableColumns = useRecordingManagementColumns();
  const {
    data,
    pagination,
    loading: gridLoading,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useRecordingManagementList({ filters });

  const audioPlayback = useAudioPlayback();

  // Action handlers
  const handleCreateRecording = useCallback(() => {
    navigate('/settings/voice-prompts/recording/new');
  }, [navigate]);

  const handleEditRecording = useCallback((recording: VoiceRecording) => {
    const idToUse = recording.uuid ?? recording.id;
    navigate(`/settings/voice-prompts/recording/${idToUse}`);
  }, [navigate]);

  const handleDeleteRecording = useCallback((recording: VoiceRecording) => {
    setRecordingToDelete(recording);
    setShowDeleteModal(true);
  }, []);

  const handlePlayRecording = useCallback(
    async (recording: VoiceRecording) => {
      try {
        await audioPlayback.handlePlay(recording);
      } catch (error) {
        showError(error.message || t('recordingManagement.errors.playFailed'));
      }
    },
    [audioPlayback, showError, t],
  );

  const handleDownloadRecording = useCallback(
    async (recording: VoiceRecording) => {
      try {
        const idToUse = recording.uuid ?? recording.id;
        await downloadRecording(idToUse, recording.filename);
        showSuccess(t('recordingManagement.messages.downloadSuccess'));
      } catch (error) {
        showError(error.message || t('recordingManagement.errors.downloadFailed'));
      }
    },
    [showSuccess, showError, t],
  );

  const confirmDelete = useCallback(async () => {
    if (!recordingToDelete) return;

    try {
      setIsDeleting(true);
      const idToUse = recordingToDelete.uuid ?? recordingToDelete.id;
      await deleteRecording(idToUse);
      showSuccess(t('recordingManagement.messages.deleteSuccess'));
      refresh();
    } catch (error) {
      showError(error.message || t('recordingManagement.errors.deleteFailed'));
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setRecordingToDelete(null);
    }
  }, [recordingToDelete, showSuccess, showError, t, refresh]);

  // Render actions for each row
  const renderActions = useCallback(
    (recording: VoiceRecording) => {
      const idToUse = recording.uuid ?? recording.id;
      const isCurrentlyPlaying = audioPlayback.playingRecordingId === idToUse;
      const isPaused = audioPlayback.isPaused;

      return (
        <RecordingManagementActions
          recording={recording}
          isCurrentlyPlaying={isCurrentlyPlaying}
          isPaused={isPaused}
          onPlay={() => handlePlayRecording(recording)}
          onDownload={() => handleDownloadRecording(recording)}
          onEdit={() => handleEditRecording(recording)}
          onDelete={() => handleDeleteRecording(recording)}
        />
      );
    },
    [audioPlayback, handlePlayRecording, handleDownloadRecording, handleEditRecording, handleDeleteRecording],
  );

  return (
    <ModulePageContainer
      currentView="list"
      listView={
        <DataGridSimple
          data={data}
          loading={gridLoading}
          rowKey="id"
          columns={availableColumns}
          pagination={pagination}
          searchQuery={searchQuery}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSearch={handleSearch}
          onSort={handleSort}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
          onColumnToggle={handleColumnToggle}
          renderActions={renderActions}
          searchPlaceholder={t('recordingManagement.searchPlaceholder') || 'Search recordings...'}
          title={t('recordingManagement.title') || 'Recording Management'}
          titleIcon={<Mic className="h-5 w-5 text-primary" />}
          emptyMessage={t('recordingManagement.noRecordings') || 'No recordings found'}
          onAdd={handleCreateRecording}
          addButtonText={t('common.add')}
          translations={{
            items: t('common:items'),
            columns: t('common:columns'),
            noItemsFound: t('common:noItemsFound'),
            showing: t('common:showing'),
            to: t('common:to'),
            of: t('common:of'),
            results: t('common:results'),
            previous: t('common:previous'),
            next: t('common:next'),
            page: t('common:page'),
            deleteItem: t('common:deleteItem'),
            areYouSureDeleteItem: t('common:areYouSureDeleteItem'),
            delete: t('common:delete'),
            cancel: t('common:cancel'),
            allSelected: t('common:allSelected'),
            selected: t('common:selected'),
            searchOptions: t('common:searchOptions'),
            noOptionsFound: t('common:noOptionsFound'),
            selectAll: t('common:selectAll'),
            clearAll: t('common:clearAll')
          }}
        />
      }
      formView={null}
    >
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title={t('recordingManagement.actions.delete')}
        message={recordingToDelete ? t('recordingManagement.messages.deleteConfirm', {
          name: recordingToDelete.name
        }) : ''}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        isLoading={isDeleting}
      />
    </ModulePageContainer>
  );
}
